import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Box, 
  IconButton, 
  Avatar, 
  Menu, 
  MenuItem, 
  Tooltip,
  CircularProgress,
  Chip
} from '@mui/material';
import AccountCircle from '@mui/icons-material/AccountCircle';
import AboutDialog from './AboutDialog';
import PaymentDialog from './PaymentDialog';
import SettingsModal from './SettingsModal';
import LoginButton from './LoginButton';

import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { useConfig } from '../hooks/useConfig';
import { fetchWithAuth } from '../utils/fetchWithAuth';

const Navigation = ({ onOpenResources }) => {
  // Get authentication state from context
  const { user, isAuthenticated, logout, loading, error } = useAuth();
  const { showNotification } = useNotification();
  
  // Get app configuration
  const { getAppTitle } = useConfig();
  
  // Debug user data and reset picture error state when user changes
  useEffect(() => {
    if (user) {
      console.log('[NAVIGATION] User data:', {
        name: user.name,
        email: user.email,
        picture: user.picture,
        pictureLength: user.picture?.length
      });
      // Reset picture error state when user changes
      setPictureLoadError(false);
    }
  }, [user]);
  
  // Menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  
  // Profile picture loading state
  const [pictureLoadError, setPictureLoadError] = useState(false);
  
  // Function to modify Google profile picture URL to potentially fix CORS issues
  const getOptimizedPictureUrl = (originalUrl) => {
    if (!originalUrl) return null;
    
    // For Google profile pictures, try different size parameters
    if (originalUrl.includes('googleusercontent.com')) {
      // Remove existing size parameter and add a smaller one
      const baseUrl = originalUrl.split('=')[0];
      return `${baseUrl}=s64-c`; // 64px size, cropped
    }
    
    return originalUrl;
  };

  // About dialog state
  const [aboutOpen, setAboutOpen] = useState(false);
  // Payment dialog state
  const [paymentOpen, setPaymentOpen] = useState(false);
  // Settings modal state
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Models state for settings modal
  const [models, setModels] = useState([]);
  
  // Load models for settings modal
  const loadModels = useCallback(async () => {
    if (!user) return;
    
    try {
      const response = await fetchWithAuth('/api/chat/models');
      if (response.ok) {
        const data = await response.json();
        setModels(data.models || []);
      }
    } catch (error) {
      console.error('[NAVIGATION] Error loading models:', error);
    }
  }, [user]);

  // Load models when user is authenticated
  useEffect(() => {
    if (user) {
      loadModels();
    }
  }, [user, loadModels]);

  // Show error notification if authentication fails
  useEffect(() => {
    if (error) {
      showNotification(`Authentication error: ${error}`, 'error');
    }
  }, [error, showNotification]);
  
  // Menu handlers
  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOpenAbout = () => {
    setAboutOpen(true);
    handleClose();
  };

  const handleCloseAbout = () => {
    setAboutOpen(false);
  };

  const handleOpenPayment = () => {
    setPaymentOpen(true);
    handleClose();
  };

  const handleClosePayment = () => {
    setPaymentOpen(false);
  };

  const handleOpenSettings = () => {
    setSettingsOpen(true);
    handleClose();
  };

  const handleCloseSettings = () => {
    setSettingsOpen(false);
  };
  
  const handleLogout = async () => {
    const success = await logout();
    handleClose();
    if (!success) {
      showNotification('Logout failed. Please try again.', 'error');
    }
  };

  return (
    <>
    <AppBar position="static">
      <Toolbar>
        <Typography 
          variant="h6" 
          component={Link} 
          to="/" 
          sx={{ 
            flexGrow: 1, 
            textDecoration: 'none', 
            color: 'inherit',
            '&:hover': {
              opacity: 0.8
            }
          }}
        >
          {getAppTitle('GeoDataBot')}
        </Typography>
        
        {isAuthenticated ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Admin indicator */}
            {user?.roles?.includes('admin') && (
              <Chip
                label="Admin"
                color="secondary"
                size="small"
                sx={{ 
                  backgroundColor: '#ff6b35',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '0.75rem'
                }}
              />
            )}
            <Tooltip title={user?.name || 'Account'}>
              <IconButton
                data-testid="user-menu"
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                color="inherit"
              >
                {user?.picture && !pictureLoadError ? (
                  <Avatar 
                    src={getOptimizedPictureUrl(user.picture)} 
                    alt={user.name || 'User'} 
                    sx={{ width: 32, height: 32 }}
                    onError={(e) => {
                      console.error('[AVATAR] Failed to load profile picture:', user.picture);
                      console.error('[AVATAR] Optimized URL:', getOptimizedPictureUrl(user.picture));
                      console.error('[AVATAR] Error details:', e);
                      console.log('[AVATAR] Falling back to AccountCircle icon');
                      setPictureLoadError(true);
                    }}
                    onLoad={() => {
                      console.log('[AVATAR] Profile picture loaded successfully:', getOptimizedPictureUrl(user.picture));
                      setPictureLoadError(false);
                    }}
                  />
                ) : (
                  <AccountCircle />
                )}
              </IconButton>
            </Tooltip>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              keepMounted
              open={open}
              onClose={handleClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem disabled>{user?.email}</MenuItem>
              <MenuItem onClick={() => {
                handleClose();
                onOpenResources();
              }}>Resources</MenuItem>
              <MenuItem onClick={handleOpenSettings}>Settings</MenuItem>
              <MenuItem onClick={handleOpenPayment}>Payment</MenuItem>
              <MenuItem onClick={handleOpenAbout}>About</MenuItem>
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </Menu>
          </Box>
        ) : (
          <Box data-testid="login-container">
            {loading ? (
              <CircularProgress color="inherit" size={24} sx={{ mr: 2 }} />
            ) : (
              <LoginButton />
            )}
          </Box>
        )}
      </Toolbar>
    </AppBar>
      <AboutDialog open={aboutOpen} onClose={handleCloseAbout} />
      <PaymentDialog open={paymentOpen} onClose={handleClosePayment} paymentInfo={null} />
      <SettingsModal 
        open={settingsOpen} 
        onClose={handleCloseSettings}
        models={models}
      />
    </>
  );
};

export default Navigation;
